// src/modules/app-views/fiscalConfig/pages/Init/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Table } from 'antd';
import { useEffect } from 'react';
import { useListContext } from 'components/core/ListProvider';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import {
  cnpjColumn,
  nameColumn,
  fullNameColumn,
  cityColumn,
  contactsColumn,
} from 'modules/app-views/companies/pages/Init/components/tableColumns';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FiscalConfigInit = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    data,
    loading,
    tableKey,
    pagination,
    handleTableChanges,
    rowSelection,
    buildTableColumns,
    availableTableColumns,
    buildNumberLineColumn,
    buildDocumentCodeColumn,
    buildCreatedAtColumn,
    buildUpdatedAtColumn,
    buildActionsButtonColumn,
    buildColumnProps,
    query,
    tableComponents,
    hiddenColumns,
    isEditingTable,
    TableFeatures,
  } = useListContext();

  const storedListSettings = useZustand('module', 'listSettings');
  const storedLanguage = useZustand('theme', 'language');

  // Serialize sort and search for stable dependencies (avoids infinite loops from function recreation)
  const sortKey = JSON.stringify(query?.sort || []);
  const searchKey = JSON.stringify(query?.search || {});

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = appService.isMobile();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Rebuild columns when sort or settings change
  useEffect(() => {
    buildTableColumns?.([
      buildNumberLineColumn(),

      buildDocumentCodeColumn(),

      cnpjColumn(buildColumnProps),

      nameColumn(buildColumnProps, { title: 'company' }),

      fullNameColumn(buildColumnProps, { title: 'company_name' }),

      cityColumn(buildColumnProps),

      contactsColumn(buildColumnProps),

      buildCreatedAtColumn(),

      buildUpdatedAtColumn(),

      buildActionsButtonColumn({ remove: false, archive: false }),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storedListSettings?.showNumberOfLine,
    sortKey,
    searchKey,
    storedLanguage,
    hiddenColumns,
    isEditingTable,
    query?.drawer?.folder,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <TableFeatures />

      <Table
        key={tableKey}
        scroll={{ x: 'max-content' }}
        bordered
        rowKey="_id"
        size={isMobile ? 'small' : 'large'}
        columns={availableTableColumns}
        dataSource={data || []}
        pagination={pagination}
        className="with-checkbox"
        loading={loading}
        onChange={handleTableChanges}
        rowSelection={rowSelection}
        components={tableComponents}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FiscalConfigInit;
